import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target,
  Globe,
  Building,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Users,
  DollarSign,
  Clock,
  Star,
  Zap,
  Brain,
  Search,
  FileText,
  BarChart3,
  Shield,
  Layers,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  Plus,
  X,
  Eye,
  MapPin,
  Lightbulb,
  Award,
  Circle,
  MinusCircle,
  Navigation,
  Crosshair,
  Compass,
  Loader2,
  Sparkles
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ProblemDefinitionPhaseProps {
  onComplete?: (results: any) => void;
  initialState?: {
    activeTab?: 'worldview' | 'context' | 'problems' | 'constraints' | 'definition';
    selectedCompetitor?: any;
    selectedChallenge?: any;
  };
  onStateChange?: (state: any) => void;
  isCompleted?: boolean;
  phaseResults?: any;
}

interface WorldViewData {
  domainContext: {
    domain: string;
    marketSize: string;
    growthRate: string;
    maturityLevel: string;
    keyTechnologies: string[];
    regulatoryEnvironment: string[];
  };
  competitors: CompetitorData[];
  marketBoundaries: {
    geographical: string[];
    technological: string[];
    regulatory: string[];
    economical: string[];
  };
  marketTrends: string[];
  opportunities: string[];
  threats: string[];
}

interface CompetitorData {
  name: string;
  marketShare: string;
  strengths: string[];
  weaknesses: string[];
  pricing: string;
  differentiator: string;
  threatLevel: 'low' | 'medium' | 'high';
  keyProducts: string[];
}

interface CompanyContext {
  background: {
    founded: string;
    employees: string;
    funding: string;
    headquarters: string;
    mission: string;
  };
  structure: {
    departments: string[];
    keyPersonnel: { role: string; name: string; expertise: string[] }[];
    decisionMaking: string;
    culture: string[];
  };
  strengths: {
    technical: string[];
    business: string[];
    strategic: string[];
  };
  existingProducts: {
    name: string;
    description: string;
    marketFit: string;
    revenue: string;
    customers: string;
  }[];
}

interface ProblemView {
  currentChallenges: {
    id: string;
    title: string;
    description: string;
    impact: 'high' | 'medium' | 'low';
    urgency: 'high' | 'medium' | 'low';
    stakeholders: string[];
    rootCause: string;
  }[];
  objectives: {
    primary: string[];
    secondary: string[];
    success_metrics: string[];
  };
  desiredBenefits: {
    business: string[];
    technical: string[];
    strategic: string[];
    customer: string[];
  };
  constraints: {
    technical: string[];
    business: string[];
    regulatory: string[];
    resource: string[];
  };
}

interface ConstraintCluster {
  id: string;
  name: string;
  position: { angle: number; distance: number };
  constraints: string[];
}

interface ConstraintCircle {
  level: 'red' | 'yellow' | 'green';
  title: string;
  description: string;
  constraints: ConstraintCluster[];
  color: string;
  radius: number;
}

interface GoodCircle {
  criteria: {
    category: string;
    minimum_standard: string;
    measurement: string;
    priority: 'critical' | 'important' | 'nice-to-have';
  }[];
  acceptance_threshold: string;
  quality_gates: string[];
}

const ProblemDefinitionPhase: React.FC<ProblemDefinitionPhaseProps> = ({
  onComplete,
  initialState,
  onStateChange,
  isCompleted,
  phaseResults
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [activeTab, setActiveTab] = useState<'worldview' | 'context' | 'problems' | 'constraints' | 'definition'>(initialState?.activeTab || 'worldview');
  const [selectedCompetitor, setSelectedCompetitor] = useState<CompetitorData | null>(initialState?.selectedCompetitor || null);
  const [selectedChallenge, setSelectedChallenge] = useState<any>(initialState?.selectedChallenge || null);
  const [selectedCluster, setSelectedCluster] = useState<ConstraintCluster | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTabCompleted, setIsTabCompleted] = useState<{[key: string]: boolean}>({});

  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [currentAnalysisStage, setCurrentAnalysisStage] = useState('');
  const [analysisLogs, setAnalysisLogs] = useState<string[]>([]);

  const navigate = useNavigate();

  const [companyData, setCompanyData] = useState<{
      company?: string;
      industry?: string;
      objective?: string;
      challenges?: string;
    }>({});
  
    useEffect(() => {
      // Read company data from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const data = {
        company: urlParams.get('company') || 'Skyflow',
        industry: urlParams.get('industry') || 'Data Privacy & AI Privacy',
        objective: urlParams.get('objective') || 'Pioneering Data Privacy Vaults to securely handle sensitive customer data with a simple API.',
        challenges: urlParams.get('challenges') || 'Intense competition, complex enterprise integration, evolving regulations, and AI privacy complexities.'
      };
      setCompanyData(data);
    }, []);
  

  const analysisStages = [
    { name: 'Analyzing World View & Market Landscape', duration: 100, progress: 20, icon: Globe },
    { name: 'Synthesizing Company Context & Strengths', duration: 100, progress: 40, icon: Building },
    { name: 'Identifying Core Problems & Objectives', duration: 100, progress: 60, icon: AlertTriangle },
    { name: 'Mapping Constraints & Boundaries', duration: 100, progress: 80, icon: MinusCircle },
    { name: 'Defining Success Criteria (Definition of Good)', duration: 100, progress: 100, icon: CheckCircle }
  ];

  // If the phase is already completed, show the content immediately
  useEffect(() => {
    if (isCompleted && phaseResults) {
      setShowContent(true);
      setAnalysisProgress(100);
      setIsTabCompleted({
        worldview: true,
        context: true,
        problems: true,
        constraints: true,
        definition: true
      });
    }
  }, [isCompleted, phaseResults]);

  const startProblemDefinition = async () => {
    setIsProcessing(true);
    setAnalysisProgress(0);
    setAnalysisLogs([]);
    setCurrentAnalysisStage('');

    for (let i = 0; i < analysisStages.length; i++) {
        const stage = analysisStages[i];
        setCurrentAnalysisStage(stage.name);
        setAnalysisLogs(prev => [...prev, `${stage.name}...`]);

        const startProgress = i > 0 ? analysisStages[i - 1].progress : 0;
        const progressSteps = 20; // Animate progress in 20 steps per stage
        const stepDuration = stage.duration / progressSteps;

        for (let j = 1; j <= progressSteps; j++) {
            await new Promise(resolve => setTimeout(resolve, stepDuration));
            setAnalysisProgress(startProgress + (stage.progress - startProgress) * (j / progressSteps));
        }
        
        setAnalysisProgress(stage.progress);
        setAnalysisLogs(prev => {
            const newLogs = [...prev];
            newLogs[newLogs.length - 1] = `${stage.name} completed`;
            return newLogs;
        });
    }

    setTimeout(() => {
        setIsProcessing(false);
        setShowContent(true);
    }, 2000);
  };

  // Save state changes for persistence
  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        activeTab,
        selectedCompetitor,
        selectedChallenge,
        isTabCompleted
      });
    }
  }, [activeTab, selectedCompetitor, selectedChallenge, isTabCompleted, onStateChange]);

  // Mark tab as visited/completed when user views it
  useEffect(() => {
    setIsTabCompleted(prev => ({ ...prev, [activeTab]: true }));
  }, [activeTab]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      
      const tabs = ['worldview', 'context', 'problems', 'constraints', 'definition'];
      const currentIndex = tabs.indexOf(activeTab);
      
      switch (event.key) {
        case 'ArrowLeft':
          if (currentIndex > 0) {
            setActiveTab(tabs[currentIndex - 1] as any);
            event.preventDefault();
          }
          break;
        case 'ArrowRight':
          if (currentIndex < tabs.length - 1) {
            setActiveTab(tabs[currentIndex + 1] as any);
            event.preventDefault();
          }
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
          const tabIndex = parseInt(event.key) - 1;
          if (tabIndex >= 0 && tabIndex < tabs.length) {
            setActiveTab(tabs[tabIndex] as any);
            event.preventDefault();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [activeTab]);

  // Comprehensive Skyflow-specific data
  const worldViewData: WorldViewData = {
    domainContext: {
      domain: 'Data Privacy & Security Technology',
      marketSize: '$12.8B by 2027',
      growthRate: '23.4% CAGR (2022-2027)',
      maturityLevel: 'Growth Stage - Rapid Evolution',
      keyTechnologies: [
        'Machine Learning & AI for PII Detection',
        'Homomorphic Encryption',
        'Differential Privacy',
        'Zero-Knowledge Proofs',
        'Federated Learning',
        'Edge Computing for Data Localization'
      ],
      regulatoryEnvironment: [
        'GDPR (General Data Protection Regulation)',
        'CCPA (California Consumer Privacy Act)',
        'HIPAA (Health Insurance Portability)',
        'SOX (Sarbanes-Oxley Act)',
        'LGPD (Lei Geral de Proteção de Dados)',
        'DPDP Act (Digital Personal Data Protection Act - India)'
      ]
    },
    competitors: [
      {
        name: 'Privacera',
        marketShare: '18%',
        strengths: ['Enterprise focus', 'Hadoop ecosystem integration', 'Policy management'],
        weaknesses: ['Limited real-time processing', 'Complex deployment', 'Legacy architecture'],
        pricing: '$200K-500K annually',
        differentiator: 'Big Data governance focus',
        threatLevel: 'medium',
        keyProducts: ['Privacera Platform', 'Data Discovery', 'Access Control']
      },
      {
        name: 'Immuta',
        marketShare: '22%',
        strengths: ['Dynamic policies', 'Data science integration', 'Governance'],
        weaknesses: ['High cost', 'Limited PII discovery', 'Performance issues'],
        pricing: '$500K+ annually',
        differentiator: 'Policy-first approach',
        threatLevel: 'high',
        keyProducts: ['Immuta Platform', 'Policy Engine', 'Data Catalog']
      },
      {
        name: 'BigID',
        marketShare: '25%',
        strengths: ['Comprehensive discovery', 'Multi-cloud', 'Classification'],
        weaknesses: ['Resource intensive', 'Complex pricing', 'Limited real-time'],
        pricing: 'Per-record pricing model',
        differentiator: 'Discovery-focused platform',
        threatLevel: 'high',
        keyProducts: ['BigID Platform', 'Data Intelligence', 'Privacy Suite']
      },
      {
        name: 'Varonis',
        marketShare: '15%',
        strengths: ['File system integration', 'Threat detection', 'Financial services'],
        weaknesses: ['Windows-centric', 'Limited cloud-native', 'Weak APIs'],
        pricing: '$100K-300K annually',
        differentiator: 'Security-first approach',
        threatLevel: 'low',
        keyProducts: ['DatAdvantage', 'Data Classification', 'Threat Detection']
      },
      {
        name: 'OneTrust',
        marketShare: '12%',
        strengths: ['Privacy management', 'Compliance workflows', 'Legal integration'],
        weaknesses: ['Limited technical depth', 'Manual processes', 'Expensive'],
        pricing: '$200K-800K annually',
        differentiator: 'Compliance-focused solution',
        threatLevel: 'medium',
        keyProducts: ['OneTrust Platform', 'Privacy Center', 'GRC Suite']
      }
    ],
    marketBoundaries: {
      geographical: [
        'North America (40% market share)',
        'Europe (35% market share)',
        'Asia-Pacific (20% market share)',
        'Rest of World (5% market share)'
      ],
      technological: [
        'Cloud-native vs Legacy on-premise',
        'Real-time vs Batch processing',
        'AI/ML-driven vs Rule-based systems',
        'API-first vs Monolithic architectures'
      ],
      regulatory: [
        'Multi-jurisdiction compliance requirements',
        'Industry-specific regulations (HIPAA, SOX)',
        'Emerging privacy laws (DPDP, LGPD)',
        'Cross-border data transfer restrictions'
      ],
      economical: [
        'Enterprise ($10M+ revenue) vs SMB markets',
        'SaaS vs On-premise licensing models',
        'Per-user vs Usage-based pricing',
        'Direct sales vs Partner channel distribution'
      ]
    },
    marketTrends: [
      'Zero-trust architecture adoption increasing 45% annually',
      'Privacy-first regulations expanding globally (15+ new laws by 2025)',
      'Real-time data processing requirements growing 67%',
      'AI/ML model privacy preservation becoming critical',
      'Edge computing driving data localization needs',
      'Quantum computing threats to current encryption methods'
    ],
    opportunities: [
      'Real-time PII detection market gap (only 23% offer sub-second response)',
      'API-first architecture demand (67% lack modern integration)',
      'Multi-cloud deployment needs (78% prefer cloud-native)',
      'Automated compliance reporting (85% manual effort reduction potential)',
      'Edge computing for data localization (emerging requirement)',
      'Developer-first adoption model (bottom-up enterprise sales)'
    ],
    threats: [
      'Large tech companies entering the market (Google, Microsoft, AWS)',
      'Open-source alternatives gaining traction',
      'Economic downturn affecting enterprise IT budgets',
      'Regulatory uncertainty and changing compliance requirements',
      'Cybersecurity threats targeting privacy infrastructure',
      'Talent shortage in privacy engineering and compliance'
    ]
  };

  const companyContext: CompanyContext = {
    background: {
      founded: '2019',
      employees: '350+ employees globally',
      funding: '$200M+ raised (Series C)',
      headquarters: 'Palo Alto, CA with offices in London, Tel Aviv',
      mission: 'Enable organizations to unlock the value of their data while preserving privacy'
    },
    structure: {
      departments: [
        'Engineering (40%)',
        'Sales & Marketing (25%)',
        'Customer Success (15%)',
        'Operations (10%)',
        'Legal & Compliance (10%)'
      ],
      keyPersonnel: [
        {
          role: 'CEO & Co-founder',
          name: 'Anshu Sharma',
          expertise: ['Enterprise Software', 'Data Privacy', 'Go-to-Market Strategy']
        },
        {
          role: 'CTO & Co-founder',
          name: 'Abhishek Agrawal',
          expertise: ['Distributed Systems', 'Security', 'Machine Learning']
        },
        {
          role: 'VP of Engineering',
          name: 'Technical Leadership Team',
          expertise: ['Cloud Architecture', 'Privacy Engineering', 'Developer Experience']
        }
      ],
      decisionMaking: 'Flat organizational structure with autonomous teams and data-driven decisions',
      culture: [
        'Privacy-first mindset',
        'Developer-centric approach',
        'Customer obsession',
        'Continuous innovation',
        'Transparency and trust'
      ]
    },
    strengths: {
      technical: [
        'Advanced API-first architecture with 99.9% uptime',
        'Real-time processing capabilities (<100ms response times)',
        'Multi-cloud deployment expertise (AWS, Azure, GCP)',
        'Strong security posture (SOC 2 Type II, ISO 27001)',
        'Scalable microservices architecture'
      ],
      business: [
        'Strong product-market fit in financial services and healthcare',
        'Experienced leadership team with enterprise software background',
        'Developer-friendly approach driving bottom-up adoption',
        'Strategic partnerships with major cloud providers',
        'Growing customer base with high retention rates (>95%)'
      ],
      strategic: [
        'Early mover advantage in privacy-preserving data infrastructure',
        'Strong IP portfolio in data privacy and security',
        'Comprehensive compliance framework covering global regulations',
        'Vision for democratizing privacy-preserving data analytics',
        'Focus on developer experience and ease of integration'
      ]
    },
    existingProducts: [
      {
        name: 'Skyflow Data Privacy Vault',
        description: 'Core platform for secure data storage and processing with PII tokenization',
        marketFit: 'Strong fit for financial services and healthcare',
        revenue: '$50M+ ARR',
        customers: '200+ enterprise customers'
      },
      {
        name: 'Skyflow APIs',
        description: 'Developer-friendly APIs for privacy-preserving data operations',
        marketFit: 'High adoption among developer teams',
        revenue: 'Included in platform pricing',
        customers: '1000+ developers using APIs'
      },
      {
        name: 'Skyflow Governance',
        description: 'Compliance and governance tools for data privacy management',
        marketFit: 'Growing demand from compliance teams',
        revenue: 'Premium add-on module',
        customers: '150+ compliance teams'
      }
    ]
  };

  const problemView: ProblemView = {
    currentChallenges: [
      {
        id: 'challenge-1',
        title: 'Real-time PII Detection at Scale',
        description: 'Current market solutions struggle with real-time PII detection and classification at enterprise scale (10M+ records/second)',
        impact: 'high',
        urgency: 'high',
        stakeholders: ['CTO', 'Engineering Teams', 'Compliance Officers', 'Data Scientists'],
        rootCause: 'Legacy batch-processing architectures and limited ML model optimization for real-time inference'
      },
      {
        id: 'challenge-2',
        title: 'Multi-Jurisdiction Compliance Complexity',
        description: 'Organizations struggle to maintain compliance across multiple regulatory frameworks (GDPR, CCPA, HIPAA, DPDP)',
        impact: 'high',
        urgency: 'medium',
        stakeholders: ['Legal Teams', 'Compliance Officers', 'Privacy Engineers', 'Business Leaders'],
        rootCause: 'Manual compliance processes and lack of automated multi-jurisdiction policy management'
      },
      {
        id: 'challenge-3',
        title: 'Integration Complexity with Existing Systems',
        description: 'Enterprise customers face 3-6 month implementation timelines due to complex integration requirements',
        impact: 'medium',
        urgency: 'high',
        stakeholders: ['Engineering Teams', 'IT Operations', 'Customer Success', 'Sales Teams'],
        rootCause: 'Lack of standardized APIs and developer-friendly integration tools'
      },
      {
        id: 'challenge-4',
        title: 'Cost Optimization for High-Volume Processing',
        description: 'Current pricing models become prohibitive for organizations with large data volumes (>1TB daily)',
        impact: 'medium',
        urgency: 'medium',
        stakeholders: ['CFO', 'Engineering Teams', 'Product Management', 'Sales Teams'],
        rootCause: 'Inefficient resource utilization and lack of intelligent auto-scaling capabilities'
      }
    ],
    objectives: {
      primary: [
        'Achieve sub-second real-time PII detection and classification at 10M+ records/second',
        'Reduce customer implementation time from 3-6 months to 2-4 weeks',
        'Enable automated multi-jurisdiction compliance across 15+ regulatory frameworks',
        'Optimize processing costs by 40-60% through intelligent resource management'
      ],
      secondary: [
        'Improve developer experience with comprehensive SDK and documentation',
        'Expand market reach to SMB segment with self-service offerings',
        'Build ecosystem partnerships with major cloud providers and SaaS platforms',
        'Establish thought leadership in privacy-preserving data analytics'
      ],
      success_metrics: [
        'Processing latency: <100ms for 99th percentile requests',
        'Detection accuracy: >99.5% for PII classification',
        'Customer satisfaction: >95% CSAT scores',
        'Implementation time: <4 weeks average',
        'System availability: 99.99% uptime SLA',
        'Cost efficiency: 50% reduction in per-record processing costs'
      ]
    },
    desiredBenefits: {
      business: [
        'Accelerated time-to-market for privacy-compliant applications',
        'Reduced compliance costs and legal risks',
        'Improved customer trust and brand reputation',
        'New revenue opportunities from privacy-preserving analytics',
        'Competitive advantage in privacy-first markets'
      ],
      technical: [
        'Simplified integration with existing data infrastructure',
        'Real-time processing capabilities for live applications',
        'Scalable architecture supporting business growth',
        'Advanced ML models for accurate PII detection',
        'Comprehensive APIs for developer productivity'
      ],
      strategic: [
        'Market leadership position in privacy technology',
        'Platform for future privacy innovation',
        'Strong ecosystem of partners and integrations',
        'Ability to expand into adjacent markets',
        'Foundation for global expansion'
      ],
      customer: [
        'Faster application development and deployment',
        'Reduced compliance burden and audit preparation time',
        'Improved data security and privacy protection',
        'Better user experience with privacy-preserving features',
        'Cost savings from automated compliance processes'
      ]
    },
    constraints: {
      technical: [
        'Must maintain backward compatibility with existing APIs',
        'Performance requirements: <100ms latency, >99.5% accuracy',
        'Multi-cloud support (AWS, Azure, GCP) required',
        'Integration with 50+ popular data platforms and tools',
        'Security standards: SOC 2 Type II, ISO 27001, FedRAMP'
      ],
      business: [
        'Budget allocation: $15M for development over 18 months',
        'Go-to-market timeline: MVP in 6 months, GA in 12 months',
        'Existing customer commitments and SLA requirements',
        'Sales team training and enablement requirements',
        'Pricing model must remain competitive with market leaders'
      ],
      regulatory: [
        'Compliance with all major privacy regulations (GDPR, CCPA, HIPAA)',
        'Regular security audits and penetration testing',
        'Data residency requirements for global customers',
        'Industry-specific compliance (FedRAMP for government)',
        'Export control regulations for international deployment'
      ],
      resource: [
        'Engineering team capacity: 60 FTE engineers available',
        'Customer success team bandwidth for migration support',
        'Infrastructure costs must not exceed 20% revenue growth',
        'Talent acquisition in competitive privacy engineering market',
        'Partnership development and ecosystem management resources'
      ]
    }
  };

  const constraintCircles: ConstraintCircle[] = [
    {
      level: 'red',
      title: 'Critical Constraints (Must NOT Do)',
      description: 'Absolute boundaries that cannot be crossed',
      color: '#dc2626',
      radius: 300,
      constraints: [
        {
          id: 'red-1',
          name: 'Security & Compliance',
          position: { angle: -75, distance: 0.85 },
          constraints: [
            'Cannot compromise data security or introduce vulnerabilities',
            'Must not violate any privacy regulations or compliance requirements',
          ]
        },
        {
          id: 'red-2',
          name: 'Customer & API Integrity',
          position: { angle: 15, distance: 0.85 },
          constraints: [
            'Cannot cause downtime or service degradation for existing customers',
            'Must not require customers to migrate away from current integrations',
            'Cannot use proprietary technologies that create vendor lock-in',
            'Must not introduce breaking changes to existing APIs'
          ]
        },
        {
          id: 'red-3',
          name: 'Budget & Timeline',
          position: { angle: 165, distance: 0.85 },
          constraints: [
            'Must not exceed $15M development budget allocation',
            'Cannot delay existing product roadmap commitments beyond Q2 2024',
          ]
        }
      ]
    },
    {
      level: 'yellow',
      title: 'Caution Zone (Proceed Carefully)',
      description: 'Areas requiring careful consideration and risk assessment',
      color: '#eab308',
      radius: 220,
      constraints: [
        {
          id: 'yellow-1',
          name: 'Architecture & Scalability',
          position: { angle: -90, distance: 0.8 },
          constraints: [
            'Avoid architectures that don\'t scale linearly with data volume',
            'Be cautious of technologies not proven at enterprise scale',
          ]
        },
        {
          id: 'yellow-2',
          name: 'Scope & Dependencies',
          position: { angle: 10, distance: 0.8 },
          constraints: [
            'Minimize dependencies on third-party services outside our control',
            'Limit scope creep beyond core PII detection functionality',
            'Limit custom integrations that require ongoing maintenance',
          ]
        },
        {
          id: 'yellow-3',
          name: 'Deployment & UX',
          position: { angle: 135, distance: 0.8 },
          constraints: [
            'Avoid complex deployment processes requiring specialized expertise',
            'Avoid UI/UX changes that require extensive user retraining'
          ]
        }
      ]
    },
    {
      level: 'green',
      title: 'Acceptable Trade-offs (Manageable Risk)',
      description: 'Areas where calculated risks can be taken',
      color: '#16a34a',
      radius: 140,
      constraints: [
        {
          id: 'green-1',
          name: 'Development & Operations',
          position: { angle: -120, distance: 0.75 },
          constraints: [
            'Acceptable to have 6-month development timeline for MVP',
            'May need additional infrastructure costs within budget limits',
            'Can deprecate non-critical legacy features with notice',
          ]
        },
        {
          id: 'green-2',
          name: 'Customer & Team Enablement',
          position: { angle: 45, distance: 0.75 },
          constraints: [
            'Can require moderate customer configuration for optimization',
            'Acceptable to have learning curve for advanced features',
            'May require additional training for customer success teams',
            'Acceptable to have regional rollout before global availability'
          ]
        },
      ]
    }
  ];

  const goodCircle: GoodCircle = {
    criteria: [
      {
        category: 'Performance',
        minimum_standard: 'Sub-100ms response time for 99th percentile requests',
        measurement: 'Real-time monitoring and SLA tracking',
        priority: 'critical'
      },
      {
        category: 'Accuracy',
        minimum_standard: '>99.5% PII detection accuracy with <0.1% false positives',
        measurement: 'Automated testing with benchmark datasets',
        priority: 'critical'
      },
      {
        category: 'Scalability',
        minimum_standard: 'Handle 10M+ records/second with linear scaling',
        measurement: 'Load testing and performance benchmarks',
        priority: 'critical'
      },
      {
        category: 'Integration',
        minimum_standard: 'Deploy in <4 weeks with standard configurations',
        measurement: 'Customer implementation tracking',
        priority: 'important'
      },
      {
        category: 'Developer Experience',
        minimum_standard: 'Comprehensive APIs with SDKs for 5+ languages',
        measurement: 'Developer satisfaction surveys and adoption metrics',
        priority: 'important'
      },
      {
        category: 'Compliance',
        minimum_standard: 'Support for 15+ regulatory frameworks out-of-the-box',
        measurement: 'Compliance audit results and certifications',
        priority: 'critical'
      },
      {
        category: 'Cost Efficiency',
        minimum_standard: '40% reduction in per-record processing costs',
        measurement: 'Cost analysis and customer ROI studies',
        priority: 'important'
      },
      {
        category: 'Availability',
        minimum_standard: '99.99% uptime with global redundancy',
        measurement: 'Infrastructure monitoring and incident tracking',
        priority: 'critical'
      }
    ],
    acceptance_threshold: 'All critical criteria must be met; 80% of important criteria must be achieved',
    quality_gates: [
      'Security review and penetration testing completion',
      'Performance benchmarks validation against targets',
      'Customer pilot program with 5+ enterprise customers',
      'Compliance certification for major regulations',
      'Load testing at 2x expected peak traffic',
      'Developer documentation and SDK completeness review'
    ]
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };
  
  const getThreatLevelColor = (level: string) => {
    switch (level) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-700 border-green-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  useEffect(() => {
    startProblemDefinition();
  }, [])

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden flex items-center justify-center">
        <div className="max-w-5xl mx-auto p-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="p-6 rounded-2xl bg-gradient-to-r from-emerald-500/20 to-green-500/20 backdrop-blur-xl border border-slate-200/50 mb-6 inline-block">
              <Target className="w-16 h-16 text-emerald-600" />
            </div>
            <h1 className="sqsft-heading-1 mb-2">Problem Definition & Context Analysis</h1>
            <p className="sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
              Comprehensive problem framing with constraints and success criteria.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Current Stage */}
            <div className="sqsft-card backdrop-blur-xl rounded-2xl p-6 border border-slate-200/50" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="sqsft-heading-3">{currentAnalysisStage}</h3>
                <div className="text-right">
                    <div className="font-bold text-lg" style={{ color: 'rgb(var(--color-deep-green))' }}>{Math.round(analysisProgress)}%</div>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                <motion.div
                  className="h-3 rounded-full"
                  style={{ backgroundColor: 'rgb(var(--color-sage-green))' }}
                  initial={{ width: 0 }}
                  animate={{ width: `${analysisProgress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
            
            {/* Analysis Logs */}
            <div className="sqsft-card backdrop-blur-xl rounded-2xl p-6 border border-slate-200/50 max-h-64 overflow-y-auto" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}>
              <h4 className="sqsft-heading-4 mb-4">Analysis Progress</h4>
              <div className="space-y-2">
                {analysisLogs.map((log, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-sm sqsft-body flex items-center space-x-2"
                  >
                    {log.endsWith('completed') ? (
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    ) : (
                      <Loader2 className="w-4 h-4 text-blue-600 flex-shrink-0 animate-spin" />
                    )}
                    <span>{log}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            
            {/* Stage Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {analysisStages.map((stage, index) => {
                const isCompleted = analysisProgress >= stage.progress;
                const isCurrent = currentAnalysisStage === stage.name;
                const Icon = stage.icon;

                return (
                  <div
                    key={stage.name}
                    className={`p-3 rounded-xl border transition-all duration-300 ${
                      isCompleted ? 'bg-green-50 border-green-200' :
                      isCurrent ? 'bg-blue-50 border-blue-200' :
                      'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-center mb-2">
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : isCurrent ? (
                        <Icon className="w-5 h-5 text-blue-600 animate-spin" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div className="text-xs text-center font-medium mb-1">{stage.name.split('(')[0]}</div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(99,102,241,0.1),transparent)] opacity-30"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(59,130,246,0.1),transparent)] opacity-30"></div>
        
        {/* Interactive mouse follower */}
        <motion.div
          className="absolute pointer-events-none z-10"
          animate={{
            x: mousePosition.x - 100,
            y: mousePosition.y - 100,
          }}
          transition={{ type: "spring", damping: 30, stiffness: 200 }}
        >
          <div className="w-48 h-48 bg-gradient-to-r from-indigo-400/5 to-blue-400/5 rounded-full blur-xl"></div>
        </motion.div>
      </div>

      <div className="relative z-20 p-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-500/20 to-green-500/20 backdrop-blur-xl border border-slate-200/50">
                  <Target className="w-10 h-10 text-emerald-600" />
                </div>
                <div>
                  <h1 className="sqsft-heading-1">
                    Problem Definition & Context Analysis
                  </h1>
                  <p className="sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                    Comprehensive problem framing with constraints and success criteria
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Progress indicator */}
               <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-center"
                >
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      const params = new URLSearchParams({
                          autostart: 'true',
                          company: companyData.company || 'Skyflow Inc.',
                          industry: companyData.industry || 'Data Privacy & Security',
                          objective: companyData.objective || '',
                          challenges: companyData.challenges || ''
                      });
                      navigate(`/business-model?${params.toString()}`);
                    }}
                    className="hidden md:flex items-center px-4 py-2 sqsft-btn-primary text-sm rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <Sparkles className="w-7 h-7 mr-4 group-hover:animate-pulse" />
                        Start Innovation Workflow
                        <ArrowRight className="w-7 h-7 ml-4 group-hover:translate-x-2 transition-transform duration-300" />
                  </motion.button>
            </motion.div>
        </div>
      </div>
    </motion.div>

          {/* Navigation Tabs with Progress Indicators */}
          <div className="mb-8">
            <div className="flex space-x-2 sqsft-card p-2 rounded-2xl backdrop-blur-xl border border-slate-200/50" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}>
              {[
                { id: 'worldview', label: 'World View', icon: Globe },
                { id: 'context', label: 'Company Context', icon: Building },
                { id: 'problems', label: 'Problem View', icon: AlertTriangle },
                { id: 'constraints', label: 'Constraints Circle', icon: MinusCircle },
                { id: 'definition', label: 'Definition of Good', icon: CheckCircle }
              ].map((tab) => {
                const isCompleted = isTabCompleted[tab.id];
                const isCurrent = activeTab === tab.id;
                
                return (
                  <motion.button
                    key={tab.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`relative flex items-center space-x-2 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                      isCurrent
                        ? 'sqsft-gradient-primary text-white border border-indigo-500/30'
                        : isCompleted
                        ? 'hover:bg-emerald-50'
                        : 'sqsft-body hover:bg-gray-100/50'
                    }`}
                    style={!isCurrent && isCompleted ? { color: 'rgb(var(--color-deep-green))' } : {}}
                  >
                    {isCompleted && !isCurrent && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full" />
                    )}
                    <tab.icon className="w-5 h-5" />
                    <span className="hidden sm:block">{tab.label}</span>
                    <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                  </motion.button>
                );
              })}
            </div>
            
            {/* Tab Progress Indicator and Quick Navigation */}
            <div className="mt-3 px-2 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="sqsft-caption">Section Progress:</span>
                  <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full transition-all duration-500"
                      style={{ width: `${(Object.keys(isTabCompleted).length / 5) * 100}%` }}
                    />
                  </div>
                  <span className="sqsft-caption">
                    {Object.keys(isTabCompleted).length}/5
                  </span>
                </div>
                
                <div className="hidden md:flex items-center space-x-4">
                  {/* Quick Navigation Arrows */}
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => {
                        const tabs = ['worldview', 'context', 'problems', 'constraints', 'definition'];
                        const currentIndex = tabs.indexOf(activeTab);
                        if (currentIndex > 0) setActiveTab(tabs[currentIndex - 1] as any);
                      }}
                      disabled={activeTab === 'worldview'}
                      className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        const tabs = ['worldview', 'context', 'problems', 'constraints', 'definition'];
                        const currentIndex = tabs.indexOf(activeTab);
                        if (currentIndex < tabs.length - 1) setActiveTab(tabs[currentIndex + 1] as any);
                      }}
                      disabled={activeTab === 'definition'}
                      className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="sqsft-caption">
                    Use ← → or 1-5 keys to navigate
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Sections */}
          <AnimatePresence mode="wait">
            {activeTab === 'worldview' && (
              <motion.div
                key="worldview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-8"
              >
                {/* Domain Context */}
                <div className="sqsft-card backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}>
                  <h2 className="sqsft-heading-2 mb-6 flex items-center">
                    <Compass className="w-6 h-6 mr-3" style={{ color: 'rgb(var(--color-deep-green))' }} />
                    Domain Context
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <h3 className="sqsft-heading-3 mb-4">Market Overview</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>Domain</span>
                          <span className="sqsft-body font-medium">{worldViewData.domainContext.domain}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Market Size</span>
                          <span className="text-emerald-600 font-bold">{worldViewData.domainContext.marketSize}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Growth Rate</span>
                          <span className="text-emerald-600 font-bold">{worldViewData.domainContext.growthRate}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Maturity</span>
                          <span className="text-gray-900 font-medium">{worldViewData.domainContext.maturityLevel}</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Technologies</h3>
                      <div className="flex flex-wrap gap-2">
                        {worldViewData.domainContext.keyTechnologies.map((tech, i) => (
                          <span key={i} className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg text-sm">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Regulatory Environment</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {worldViewData.domainContext.regulatoryEnvironment.map((reg, i) => (
                        <div key={i} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                          <Shield className="w-4 h-4 text-orange-600 flex-shrink-0" />
                          <span className="text-gray-700 text-sm">{reg}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Competitive Landscape */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Users className="w-6 h-6 mr-3 text-indigo-600" />
                    Competitive Landscape
                  </h2>
                  <div className="space-y-4">
                    {worldViewData.competitors.map((competitor, index) => (
                      <motion.div
                        key={competitor.name}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => setSelectedCompetitor(competitor)}
                        className="bg-white/60 rounded-xl p-6 border border-slate-200/50 hover:border-indigo-300 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-1">{competitor.name}</h3>
                            <p className="text-gray-600">{competitor.differentiator}</p>
                          </div>
                          <div className="flex items-center space-x-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getThreatLevelColor(competitor.threatLevel)}`}>
                              {competitor.threatLevel} threat
                            </span>
                            <span className="text-indigo-600 font-bold">{competitor.marketShare}</span>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="text-gray-900 font-semibold mb-2">Strengths</h4>
                            <div className="space-y-1">
                              {competitor.strengths.slice(0, 3).map((strength, i) => (
                                <div key={i} className="flex items-start space-x-2">
                                  <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                                  <span className="text-gray-700 text-sm">{strength}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h4 className="text-gray-900 font-semibold mb-2">Weaknesses</h4>
                            <div className="space-y-1">
                              {competitor.weaknesses.slice(0, 3).map((weakness, i) => (
                                <div key={i} className="flex items-start space-x-2">
                                  <X className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                  <span className="text-gray-700 text-sm">{weakness}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-4 flex items-center justify-between">
                          <div className="text-gray-600 text-sm">
                            Pricing: {competitor.pricing}
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Market Boundaries */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <MapPin className="w-6 h-6 mr-3 text-indigo-600" />
                    Market Boundaries
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {Object.entries(worldViewData.marketBoundaries).map(([category, boundaries]) => (
                      <div key={category}>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 capitalize">{category}</h3>
                        <div className="space-y-2">
                          {boundaries.map((boundary, i) => (
                            <div key={i} className="flex items-start space-x-2 p-3 bg-gray-50 rounded-lg">
                              <Navigation className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700 text-sm">{boundary}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'context' && (
              <motion.div
                key="context"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-8"
              >
                {/* Company Background */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Building className="w-6 h-6 mr-3 text-indigo-600" />
                    Company Background
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      {Object.entries(companyContext.background).map(([key, value]) => (
                        <div key={key}>
                          <div className="text-gray-600 text-sm capitalize">{key.replace(/([A-Z])/g, ' $1')}</div>
                          <div className="text-gray-900 font-medium">{value}</div>
                        </div>
                      ))}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Culture & Values</h3>
                      <div className="space-y-2">
                        {companyContext.structure.culture.map((value, i) => (
                          <div key={i} className="flex items-center space-x-2">
                            <Star className="w-4 h-4 text-yellow-600" />
                            <span className="text-gray-700">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Organizational Structure */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Users className="w-6 h-6 mr-3 text-indigo-600" />
                    Organizational Structure
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Distribution</h3>
                      <div className="space-y-3">
                        {companyContext.structure.departments.map((dept, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-gray-700">{dept.split(' (')[0]}</span>
                            <span className="text-emerald-600 font-bold">{dept.match(/\((.+)\)/)?.[1] || ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Personnel</h3>
                      <div className="space-y-4">
                        {companyContext.structure.keyPersonnel.map((person, i) => (
                          <div key={i} className="p-4 bg-gray-50 rounded-lg">
                            <div className="text-gray-900 font-semibold">{person.role}</div>
                            <div className="text-gray-600 text-sm mb-2">{person.name}</div>
                            <div className="flex flex-wrap gap-1">
                              {person.expertise.map((skill, j) => (
                                <span key={j} className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Company Strengths */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Zap className="w-6 h-6 mr-3 text-indigo-600" />
                    Core Strengths
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {Object.entries(companyContext.strengths).map(([category, strengths]) => (
                      <div key={category}>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 capitalize">{category}</h3>
                        <div className="space-y-2">
                          {strengths.map((strength, i) => (
                            <div key={i} className="flex items-start space-x-2 p-3 bg-gray-50 rounded-lg">
                              <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700 text-sm">{strength}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Existing Products */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Layers className="w-6 h-6 mr-3 text-indigo-600" />
                    Existing Products
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {companyContext.existingProducts.map((product, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.1 }}
                        className="p-6 bg-white/60 rounded-xl border border-slate-200/50 shadow-sm"
                      >
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{product.name}</h3>
                        <p className="text-gray-600 text-sm mb-4">{product.description}</p>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 text-xs">Market Fit</span>
                            <span className="text-indigo-600 text-sm">{product.marketFit}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 text-xs">Revenue</span>
                            <span className="text-emerald-600 font-bold text-sm">{product.revenue}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 text-xs">Customers</span>
                            <span className="text-blue-600 text-sm">{product.customers}</span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'problems' && (
              <motion.div
                key="problems"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-8"
              >
                {/* Current Challenges */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <AlertTriangle className="w-6 h-6 mr-3 text-red-600" />
                    Current Challenges
                  </h2>
                  <div className="space-y-4">
                    {problemView.currentChallenges.map((challenge, index) => (
                      <motion.div
                        key={challenge.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => setSelectedChallenge(challenge)}
                        className="p-6 bg-white/60 rounded-xl border border-slate-200/50 hover:border-red-300 transition-all duration-300 cursor-pointer shadow-sm"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{challenge.title}</h3>
                            <p className="text-gray-600 text-sm">{challenge.description}</p>
                          </div>
                          <div className="flex items-center space-x-4 ml-4">
                            <div className="text-center">
                              <div className={`text-sm font-bold ${getImpactColor(challenge.impact)}`}>
                                {challenge.impact}
                              </div>
                              <div className="text-gray-500 text-xs">Impact</div>
                            </div>
                            <div className="text-center">
                              <div className={`text-sm font-bold ${getImpactColor(challenge.urgency)}`}>
                                {challenge.urgency}
                              </div>
                              <div className="text-gray-500 text-xs">Urgency</div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <div className="text-gray-500 text-xs mb-1">Root Cause</div>
                          <div className="text-gray-700 text-sm">{challenge.rootCause}</div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex flex-wrap gap-1">
                            {challenge.stakeholders.slice(0, 3).map((stakeholder, i) => (
                              <span key={i} className="px-2 py-1 bg-red-500/20 text-red-300 rounded text-xs">
                                {stakeholder}
                              </span>
                            ))}
                            {challenge.stakeholders.length > 3 && (
                              <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded text-xs">
                                +{challenge.stakeholders.length - 3} more
                              </span>
                            )}
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Objectives & Benefits */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <Target className="w-6 h-6 mr-3 text-indigo-600" />
                      Objectives
                    </h2>
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Primary Objectives</h3>
                        <div className="space-y-2">
                          {problemView.objectives.primary.map((objective, i) => (
                            <div key={i} className="flex items-start space-x-2 p-3 bg-indigo-50 rounded-lg">
                              <Star className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700 text-sm">{objective}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Success Metrics</h3>
                        <div className="space-y-2">
                          {problemView.objectives.success_metrics.map((metric, i) => (
                            <div key={i} className="flex items-start space-x-2 p-3 bg-emerald-50 rounded-lg">
                              <BarChart3 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700 text-sm">{metric}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <TrendingUp className="w-6 h-6 mr-3 text-indigo-600" />
                      Desired Benefits
                    </h2>
                    <div className="space-y-4">
                      {Object.entries(problemView.desiredBenefits).map(([category, benefits]) => (
                        <div key={category}>
                          <h3 className="text-gray-900 font-semibold mb-2 capitalize">{category}</h3>
                          <div className="space-y-1">
                            {benefits.slice(0, 3).map((benefit, i) => (
                              <div key={i} className="text-gray-700 text-sm flex items-start space-x-2">
                                <ChevronRight className="w-3 h-3 text-emerald-600 mt-1 flex-shrink-0" />
                                <span>{benefit}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'constraints' && (
              <motion.div
                key="constraints"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-8"
              >
                {/* Concentric Circles Visualization */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Circle className="w-6 h-6 mr-3 text-red-600" />
                    Constraints Circle - Three Concentric Boundaries
                  </h2>
                  
                  <div className="relative flex items-center justify-center min-h-[600px] w-full max-w-[800px] mx-auto bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200/30">
                    {/* SVG Circles */}
                    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 600">
                      {constraintCircles.map((circle, index) => (
                        <motion.circle
                          key={circle.level}
                          cx="400"
                          cy="300"
                          r={circle.radius}
                          fill={`${circle.color}15`}
                          stroke={circle.color}
                          strokeWidth="3"
                          strokeDasharray={circle.level === 'red' ? '10,5' : circle.level === 'yellow' ? '8,4' : 'none'}
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: index * 0.3, duration: 0.8, ease: "easeOut" }}
                        />
                      ))}
                      
                      {/* Center point */}
                      <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1, duration: 0.5 }}>
                        <circle cx="400" cy="300" r="15" fill="#10b981" stroke="#059669" strokeWidth="3" />
                        <text x="400" y="305" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">★</text>
                      </motion.g>
                    </svg>
                    
                    {/* Legend */}
                    <div className="absolute top-4 left-4 space-y-2 z-10">
                      {constraintCircles.map((circle, index) => (
                        <motion.div
                          key={circle.level}
                          className="flex items-center space-x-2 px-3 py-2 bg-white/90 backdrop-blur-sm rounded-lg border border-slate-200/50 shadow-sm"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 + index * 0.2 }}
                        >
                          <div 
                            className="w-4 h-4 rounded-full border-2"
                            style={{ backgroundColor: circle.color, borderColor: circle.color }}
                          />
                          <span className="text-gray-700 font-medium text-sm">{circle.title.split(' ')[0]}</span>
                        </motion.div>
                      ))}
                    </div>
                    
                    {/* Constraint Clusters */}
                    {constraintCircles.map((circle) => 
                      circle.constraints.map((cluster, clusterIndex) => {
                        const angleRad = (cluster.position.angle * Math.PI) / 180;
                        const x = 400 + Math.cos(angleRad) * circle.radius * cluster.position.distance;
                        const y = 300 + Math.sin(angleRad) * circle.radius * cluster.position.distance;

                        return (
                          <motion.div
                            key={cluster.id}
                            className="absolute z-10 transform -translate-x-1/2 -translate-y-1/2"
                            style={{ left: `${(x/800)*100}%`, top: `${(y/600)*100}%` }}
                            initial={{ opacity: 0, scale: 0.5 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.8 + clusterIndex * 0.2, duration: 0.5 }}
                          >
                            <motion.div
                              onClick={() => setSelectedCluster(cluster)}
                              whileHover={{ scale: 1.1, zIndex: 50 }}
                              whileTap={{ scale: 0.95 }}
                              className="flex items-center space-x-2 px-3 py-2 rounded-lg shadow-lg cursor-pointer border-2 border-white/50 backdrop-blur-sm"
                              style={{ 
                                backgroundColor: `${circle.color}CC`,
                                borderColor: 'rgba(255,255,255,0.8)'
                              }}
                            >
                              <div 
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: 'white' }}
                              />
                              <span className="text-white font-semibold text-xs whitespace-nowrap">
                                {cluster.name}
                              </span>
                              <div 
                                className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold"
                              >
                                {cluster.constraints.length}
                              </div>
                            </motion.div>
                          </motion.div>
                        )
                      })
                    )}
                    
                    {/* Center label */}
                    <motion.div
                      className="absolute top-1/2 left-1/2 transform -translate-x-1/2 translate-y-8 z-20 pointer-events-none"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1.5 }}
                    >
                      <div className="text-center bg-emerald-500/95 px-4 py-2 rounded-xl border-2 border-white/50 shadow-xl backdrop-blur-sm">
                        <div className="text-white font-bold text-sm">Solution Space</div>
                        <div className="text-emerald-100 text-xs">Innovation Zone</div>
                      </div>
                    </motion.div>
                  </div>
                  
                  <div className="mt-4 text-center">
                    <div className="inline-flex items-center space-x-2 text-gray-600 text-sm">
                      <Eye className="w-4 h-4" />
                      <span>Click on constraint clusters to view details</span>
                    </div>
                  </div>
                </div>

                {/* Detailed Constraints Analysis */}
                <div className="space-y-6 mt-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
                    <FileText className="w-6 h-6 mr-3 text-indigo-600" />
                    Detailed Constraint Analysis
                  </h2>
                  {constraintCircles.map((circle, index) => (
                    <motion.div
                      key={circle.level}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + index * 0.2 }}
                      className="bg-white/60 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-6 shadow-sm"
                    >
                      <div className="flex items-start space-x-4 mb-4">
                        <div 
                          className="w-10 h-10 rounded-full border-4 flex-shrink-0 flex items-center justify-center"
                          style={{ borderColor: circle.color, backgroundColor: `${circle.color}20` }}
                        >
                           <div className="w-3 h-3 rounded-full" style={{ backgroundColor: circle.color }}/>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold" style={{ color: circle.color }}>{circle.title}</h3>
                             <p className="text-gray-600 text-sm">{circle.description}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {circle.constraints.map((cluster) => (
                          <div key={cluster.id} className="p-4 bg-gray-50/50 rounded-lg border border-slate-200/50">
                             <h4 className="font-semibold text-gray-800 mb-3">{cluster.name}</h4>
                             <div className="space-y-2">
                                {cluster.constraints.map((constraint, i) => (
                                  <div key={i} className="flex items-start space-x-3">
                                    <MinusCircle 
                                      className="w-4 h-4 mt-0.5 flex-shrink-0"
                                      style={{ color: circle.color }}
                                    />
                                    <span className="text-gray-700 text-sm">{constraint}</span>
                                  </div>
                                ))}
                             </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {activeTab === 'definition' && (
              <motion.div
                key="definition"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-8"
              >
                {/* Definition of Good */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <CheckCircle className="w-6 h-6 mr-3 text-emerald-600" />
                    Definition of "Good Enough" - Minimum Acceptable Standards
                  </h2>
                  
                  <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <h3 className="text-emerald-700 font-semibold mb-2">Acceptance Threshold</h3>
                    <p className="text-emerald-800">{goodCircle.acceptance_threshold}</p>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {goodCircle.criteria.map((criterion, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`p-6 rounded-xl border ${
                          criterion.priority === 'critical' 
                            ? 'bg-red-50 border-red-200' 
                            : criterion.priority === 'important'
                            ? 'bg-yellow-50 border-yellow-200'
                            : 'bg-blue-50 border-blue-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-lg font-bold text-gray-900">{criterion.category}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            criterion.priority === 'critical' 
                              ? 'bg-red-100 text-red-700' 
                              : criterion.priority === 'important'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {criterion.priority}
                          </span>
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <div className="text-gray-600 text-sm">Minimum Standard</div>
                            <div className="text-gray-900 font-medium">{criterion.minimum_standard}</div>
                          </div>
                          <div>
                            <div className="text-gray-600 text-sm">Measurement Method</div>
                            <div className="text-gray-700">{criterion.measurement}</div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Quality Gates */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Shield className="w-6 h-6 mr-3 text-indigo-600" />
                    Quality Gates & Validation Checkpoints
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {goodCircle.quality_gates.map((gate, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-4 bg-gray-50 rounded-xl border border-slate-200 hover:border-indigo-300 transition-all duration-300"
                      >
                        <div className="flex items-start space-x-3">
                          <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-bold mt-1">
                            {index + 1}
                          </div>
                          <div>
                            <div className="text-gray-900 font-medium">{gate}</div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Success Criteria Summary */}
                <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Award className="w-6 h-6 mr-3 text-indigo-600" />
                    Success Criteria Summary
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-red-50 border border-red-200 rounded-xl">
                      <div className="text-3xl font-bold text-red-600 mb-2">
                        {goodCircle.criteria.filter(c => c.priority === 'critical').length}
                      </div>
                      <div className="text-red-700 font-medium">Critical Criteria</div>
                      <div className="text-gray-600 text-sm">Must be 100% achieved</div>
                    </div>
                    
                    <div className="text-center p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
                      <div className="text-3xl font-bold text-yellow-600 mb-2">
                        {goodCircle.criteria.filter(c => c.priority === 'important').length}
                      </div>
                      <div className="text-yellow-700 font-medium">Important Criteria</div>
                      <div className="text-gray-600 text-sm">80% achievement required</div>
                    </div>
                    
                    <div className="text-center p-6 bg-blue-50 border border-blue-200 rounded-xl">
                      <div className="text-3xl font-bold text-blue-600 mb-2">
                        {goodCircle.quality_gates.length}
                      </div>
                      <div className="text-blue-700 font-medium">Quality Gates</div>
                      <div className="text-gray-600 text-sm">Validation checkpoints</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Competitor Detail Modal */}
      <AnimatePresence>
        {selectedCompetitor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedCompetitor(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 rounded-2xl p-8 max-w-3xl max-h-[80vh] overflow-y-auto border border-white/10"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">{selectedCompetitor.name}</h2>
                <button
                  onClick={() => setSelectedCompetitor(null)}
                  className="text-slate-500 hover:text-white transition-colors text-xl"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-slate-400 text-sm">Market Share</div>
                    <div className="text-orange-400 font-bold text-lg">{selectedCompetitor.marketShare}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Threat Level</div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getThreatLevelColor(selectedCompetitor.threatLevel)}`}>
                      {selectedCompetitor.threatLevel}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-white font-semibold mb-3">Strengths</h3>
                    <div className="space-y-2">
                      {selectedCompetitor.strengths.map((strength, i) => (
                        <div key={i} className="flex items-start space-x-2 p-3 bg-emerald-500/10 rounded-lg">
                          <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-300 text-sm">{strength}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-white font-semibold mb-3">Weaknesses</h3>
                    <div className="space-y-2">
                      {selectedCompetitor.weaknesses.map((weakness, i) => (
                        <div key={i} className="flex items-start space-x-2 p-3 bg-red-500/10 rounded-lg">
                          <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-300 text-sm">{weakness}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-white font-semibold mb-3">Key Products</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedCompetitor.keyProducts.map((product, i) => (
                      <span key={i} className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-lg text-sm">
                        {product}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="text-slate-400 text-sm">Pricing Model</div>
                  <div className="text-white font-medium">{selectedCompetitor.pricing}</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Challenge Detail Modal */}
      <AnimatePresence>
        {selectedChallenge && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedChallenge(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 rounded-2xl p-8 max-w-3xl max-h-[80vh] overflow-y-auto border border-white/10"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">{selectedChallenge.title}</h2>
                <button
                  onClick={() => setSelectedChallenge(null)}
                  className="text-slate-500 hover:text-white transition-colors text-xl"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-white font-semibold mb-2">Description</h3>
                  <p className="text-slate-300">{selectedChallenge.description}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-slate-400 text-sm">Impact Level</div>
                    <div className={`font-bold ${getImpactColor(selectedChallenge.impact)}`}>
                      {selectedChallenge.impact.toUpperCase()}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Urgency Level</div>
                    <div className={`font-bold ${getImpactColor(selectedChallenge.urgency)}`}>
                      {selectedChallenge.urgency.toUpperCase()}
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-white font-semibold mb-3">Root Cause Analysis</h3>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <p className="text-slate-300">{selectedChallenge.rootCause}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-white font-semibold mb-3">Affected Stakeholders</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedChallenge.stakeholders.map((stakeholder: string, i: number) => (
                      <span key={i} className="px-3 py-1 bg-red-500/20 text-red-300 rounded-lg text-sm">
                        {stakeholder}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Constraint Cluster Detail Modal */}
      <AnimatePresence>
        {selectedCluster && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedCluster(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-white/10 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-5 h-5 rounded-full"
                    style={{ 
                      backgroundColor: constraintCircles.find(c => c.constraints.some(con => con.id === selectedCluster.id))?.color
                    }}
                  />
                  <h2 className="text-2xl font-bold text-white">{selectedCluster.name}</h2>
                </div>
                <button
                  onClick={() => setSelectedCluster(null)}
                  className="text-slate-500 hover:text-white transition-colors text-xl"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-3">
                <h3 className="text-slate-300 font-semibold mb-2">Constraints in this category:</h3>
                {selectedCluster.constraints.map((constraint, i) => (
                  <div key={i} className="flex items-start space-x-3 p-4 bg-slate-800/50 rounded-lg">
                    <MinusCircle 
                      className="w-5 h-5 mt-0.5 flex-shrink-0" 
                      style={{ 
                        color: constraintCircles.find(c => c.constraints.some(con => con.id === selectedCluster.id))?.color
                      }}
                    />
                    <span className="text-slate-200">{constraint}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProblemDefinitionPhase;